import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import HintIcon from "@/components/HintIcon";
import AgenticSummaryTile from "@/components/AgenticSummaryTile";
import MetaTemplatesWarningBanner from "@/components/MetaTemplatesWarningBanner";
import {
  Users2, TrendingUp, CheckCircle2, AlertTriangle,
  ArrowUpRight, RefreshCw, MessageSquare, Zap, Clock, Database,
} from "lucide-react";

const PageHeader = ({ title, sub, right }) => (
  <div className="flex items-start justify-between gap-4 mb-8">
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Command Centre</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">{title}</h1>
      {sub && <p className="text-sm text-slate-600 mt-1">{sub}</p>}
    </div>
    <div>{right}</div>
  </div>
);

/** Compact human-friendly relative time — "5m ago", "3h ago", "yesterday". */
const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "—";
  }
};

const Stat = ({ testId, label, value, sub, icon: Icon, tone = "default" }) => {
  const tones = {
    default: "text-slate-900",
    primary: "text-orange-600",
    accent: "text-indigo-600",
    danger: "text-red-600",
    ok: "text-emerald-600",
  };
  return (
    <div
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-lg p-5 hover:-translate-y-[1px] hover:shadow-sm transition-transform duration-200"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className={`mt-2 font-heading font-extrabold text-3xl tabular ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null); // v29.4
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/dashboard/stats");
    setStats(data);
    try {
      const { data: ss } = await api.get("/admin/sync-status");
      setSyncStatus(ss);
    } catch { /* non-critical */ }
  };

  useEffect(() => {
    load();
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/trial-users/sync");
      const skipped = data.skipped_by_cutoff || 0;
      toast.success(
        skipped > 0
          ? `Synced ${data.synced} users · ${skipped} pre-cutoff skipped`
          : `Synced ${data.synced} users from master DB`
      );
      await load();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (!stats) return <div className="p-10 text-sm text-slate-500">Loading dashboard…</div>;

  const t = stats.totals;
  return (
    <div data-testid={DASHBOARD.root} className="p-8 lg:p-12 max-w-[1400px]">
      <PageHeader
        title="Conversion Dashboard"
        sub={`Real-time view of the trial-to-paid funnel. ${t.trial} users currently in trial window.`}
        right={
          <div className="flex items-center gap-2">
            <button
              data-testid={DASHBOARD.syncBtn}
              onClick={sync}
              disabled={syncing}
              className="inline-flex items-center gap-2 border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-2 rounded-md hover:bg-slate-50 transition-colors duration-150"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
              Sync from Master DB
            </button>
          </div>
        }
      />

      <AgenticSummaryTile />

      <MetaTemplatesWarningBanner />

      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button onClick={() => navigate("/users")} className="text-left" title="Cumulative total of all users ever synced from the PrepHacker Master DB (not just this sync's new ones).">
          <Stat testId={DASHBOARD.statTotal} label="Total Users" value={t.total} icon={Users2} sub="all-time (cumulative)" />
        </button>
        <button onClick={() => navigate("/users?status=trial")} className="text-left">
          <Stat testId={DASHBOARD.statTrial} label="In Trial" value={t.trial} icon={Clock} sub="active 3-day window" tone="primary" />
        </button>
        <button onClick={() => navigate("/users?status=paid")} className="text-left">
          <Stat testId={DASHBOARD.statPaid} label="Converted" value={t.paid} icon={CheckCircle2} sub={`${stats.conversion_rate}% conversion`} tone="ok" />
        </button>
        <button onClick={() => navigate("/users?escalated=true")} className="text-left">
          <Stat testId={DASHBOARD.statConversion} label="Escalated" value={t.escalated} icon={AlertTriangle} sub="needs human review" tone="danger" />
        </button>
      </div>

      {/* v29.4/v29.6 — Sync Health tile */}
      {syncStatus && (
        <div
          data-testid="dashboard-sync-health-tile"
          onClick={() => navigate("/settings")}
          className={`mt-4 bg-white border rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:shadow-sm transition-shadow ${
            syncStatus.is_failing
              ? "border-red-400 bg-red-50/40"
              : syncStatus.provider === "mock"
                ? "border-amber-300"
                : "border-slate-200"
          }`}
          title="Click to open Master DB sync settings"
        >
          <div className={`h-10 w-10 rounded-md flex items-center justify-center ${
            syncStatus.is_failing
              ? "bg-red-100 text-red-700"
              : syncStatus.provider === "mock" ? "bg-amber-50 text-amber-700" : "bg-indigo-50 text-indigo-700"
          }`}>
            {syncStatus.is_failing ? <AlertTriangle className="h-5 w-5" /> : <Database className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">Master DB sync</span>
              {syncStatus.is_failing && (
                <span
                  data-testid="sync-health-failing-badge"
                  className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-red-600 text-white border-red-700 animate-pulse"
                >
                  Failing
                </span>
              )}
              <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                syncStatus.provider === "mock"
                  ? "bg-amber-50 text-amber-800 border-amber-200"
                  : "bg-indigo-50 text-indigo-700 border-indigo-200"
              }`}>{syncStatus.provider}</span>
              <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                syncStatus.sync_enabled
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-slate-100 text-slate-500 border-slate-200"
              }`}>{syncStatus.sync_enabled ? `Auto every ${syncStatus.interval_minutes}m` : "Auto OFF"}</span>
              {syncStatus.cutoff_date && (
                <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">
                  From {new Date(syncStatus.cutoff_date).toLocaleDateString()}
                </span>
              )}
            </div>

            {syncStatus.is_failing ? (
              <>
                <div className="text-sm text-red-700 font-semibold mt-1" data-testid="sync-health-failure-line">
                  Last {syncStatus.last_failure_kind === "auto" ? "auto" : "manual"} sync failed{" "}
                  <b>{timeAgo(syncStatus.last_failure_at)}</b>
                </div>
                {syncStatus.last_failure_error && (
                  <div
                    data-testid="sync-health-failure-error"
                    className="text-[11px] text-red-800 mt-1 font-mono bg-red-100/60 px-2 py-1 rounded max-w-full truncate"
                    title={syncStatus.last_failure_error}
                  >
                    {syncStatus.last_failure_error}
                  </div>
                )}
                {syncStatus.last_run && (
                  <div className="text-[10.5px] text-slate-500 mt-1">
                    Last successful run <b>{timeAgo(syncStatus.last_run)}</b>{" "}
                    (+{syncStatus.last_synced} synced).
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-slate-700 mt-1">
                {syncStatus.last_run ? (
                  <>
                    Last {syncStatus.last_kind === "auto" ? "auto" : "manual"} run{" "}
                    <b>{timeAgo(syncStatus.last_run)}</b>
                    {" · "}
                    <span className="text-emerald-700 font-semibold" data-testid="sync-health-last-synced">
                      +{syncStatus.last_synced} synced
                    </span>
                    {(syncStatus.last_skipped_by_cutoff || 0) > 0 && (
                      <>
                        {" · "}
                        <span className="text-amber-700 font-semibold" data-testid="sync-health-last-skipped">
                          {syncStatus.last_skipped_by_cutoff} skipped by cutoff
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="text-slate-500 italic">No sync run yet.</span>
                )}
              </div>
            )}

            {syncStatus.provider === "mock" && !syncStatus.is_failing && (
              <div className="text-[10.5px] text-amber-800 mt-1">
                <b>Currently on mock provider.</b> Set <code className="font-mono bg-white px-1 rounded">MASTER_DATA_PROVIDER=mariadb</code> + MariaDB creds on the server to connect the real DB.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Funnel */}
      <div className="mt-6 bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-2">
            Trial Funnel
            <HintIcon
              testId="dashboard-funnel-hint"
              hint={
                <>
                  How your users are distributed across the 3-day trial and post-trial states.<br /><br />
                  <b>Day 1 / Day 2 / Day 3</b> — active trial by hours since signup.<br />
                  <b className="text-emerald-700">Active</b> — past 72h, unpaid, opened the app in the last 7 days.<br />
                  <b className="text-slate-700">Inactive</b> — past 72h, unpaid, no login for 7+ days.<br /><br />
                  <span className="text-slate-500">Click any tile to see the underlying users.</span>
                </>
              }
            />
          </div>
          <div className="text-xs text-slate-500 inline-flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" />
            {stats.messages_today} messages last 24h · {stats.ai_messages_today} by AI
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            ["Day 1 (0-24h)", stats.funnel.day1, "bg-emerald-500", DASHBOARD.funnelDay1, "day1",
              "New trial users in their first day. AI kicks off welcome + feature intro touchpoints."],
            ["Day 2 (24-48h)", stats.funnel.day2, "bg-orange-500", DASHBOARD.funnelDay2, "day2",
              "Users are getting familiar. AI shares social proof & handles early objections."],
            ["Day 3 (48-72h)", stats.funnel.day3, "bg-red-500", DASHBOARD.funnelDay3, "day3",
              "Final trial day. AI applies urgency + pricing nudges before the trial ends."],
            ["Active", stats.funnel.active || 0, "bg-emerald-600", DASHBOARD.funnelActive, "active",
              "Past the 72h trial, not converted yet, but opened the app within the last 7 days. Highest-priority for the win-back cadence."],
            ["Inactive", stats.funnel.inactive || 0, "bg-slate-400", DASHBOARD.funnelInactive, "inactive",
              "Past the 72h trial, not converted, and no login for 7+ days. Cold leads — worth a low-touch reactivation nudge."],
          ].map(([label, count, color, tid, bucket, hint]) => (
            <div
              key={label}
              className="relative border border-slate-200 rounded-md p-4 hover:border-orange-400 hover:shadow-sm transition-all duration-150"
            >
              <button
                onClick={() => navigate(`/users?bucket=${bucket}`)}
                data-testid={tid}
                className="text-left w-full"
                title={hint}
              >
                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</span>
                  <span className={`h-2 w-2 rounded-full ${color} ml-auto`} />
                </div>
                <div className="font-heading font-extrabold text-3xl tabular mt-2">{count}</div>
              </button>
              <div className="absolute top-2 right-2">
                <HintIcon hint={hint} size="sm" testId={`${tid}-hint`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom grid */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div data-testid={DASHBOARD.topLeadsCard} className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Top Leads</div>
              <div className="font-heading text-lg font-bold tracking-tight">Ranked by overall engagement</div>
              <div className="text-[10px] text-slate-500 mt-0.5">score + inbound replies + recency</div>
            </div>
            <Link to="/users" className="text-xs text-orange-600 font-semibold inline-flex items-center gap-1 hover:underline">
              All users <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="space-y-2">
            {stats.top_leads.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-md">
                No leads scored yet. Users will be scored automatically as they reply.
              </div>
            )}
            {stats.top_leads.map((u) => (
              <Link
                key={u.id}
                to={`/users/${u.id}`}
                state={{ from: "/" }}
                className="flex items-center justify-between p-3 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors duration-150"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.exam} · {u.city}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-xs" title="reply count">
                    <span className="text-slate-500">💬</span>
                    <span className="font-mono font-semibold">{u._reply_count ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <Zap className="h-3.5 w-3.5 text-orange-500" />
                    <span className="font-mono font-semibold">{u.conversion_score}</span>
                  </div>
                  <div className="text-[10px] px-2 py-0.5 rounded bg-slate-100 uppercase tracking-widest font-semibold text-slate-600">
                    {u.subscription_status}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div data-testid={DASHBOARD.activityCard} className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="mb-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Live Activity</div>
            <div className="font-heading text-lg font-bold tracking-tight">Recent events</div>
          </div>
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
            {stats.recent_activity.slice(0, 12).map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-slate-200 pl-3 py-1">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-3 w-3 text-slate-400" />
                  <span className="font-mono font-semibold text-slate-700">{a.action}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {new Date(a.created_at).toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
